/**
 * TouchInput — virtual joystick + action buttons for mobile.
 *
 * Provides the same getActions() interface as InputLocal so the
 * Game engine can swap seamlessly.
 *
 * Layout:
 *   Left side:  virtual joystick (move)
 *   Right side: 3 weapon buttons (Rock, Paper, Scissors) + Dash button
 */
export class TouchInput {
  constructor() {
    // ── State ──
    this.moveX = 0;
    this.moveY = 0;
    this._rockPressed = false;
    this._paperHeld = false;
    this._scissorsPressed = false;
    this._dashPressed = false;
    this._rockJust = false;
    this._scissorsJust = false;
    this._dashJust = false;
    this._paperJust = false;

    // Joystick tracking
    this._joystickTouchId = null;
    this._joystickOrigin = { x: 0, y: 0 };
    this._joystickPos = { x: 0, y: 0 };
    this._joystickActive = false;

    this._createUI();
    this._setupListeners();
  }

  _createUI() {
    // Container
    this.container = document.createElement('div');
    this.container.id = 'touch-controls';
    this.container.innerHTML = `
      <div id="joystick-zone">
        <div id="joystick-base">
          <div id="joystick-thumb"></div>
        </div>
      </div>
      <div id="action-buttons">
        <button class="action-btn" id="btn-dash" data-action="dash">💨</button>
        <div class="weapon-buttons">
          <button class="action-btn weapon" id="btn-rock" data-action="rock">🪨</button>
          <button class="action-btn weapon" id="btn-paper" data-action="paper">📄</button>
          <button class="action-btn weapon" id="btn-scissors" data-action="scissors">✂️</button>
        </div>
      </div>
    `;

    const style = document.createElement('style');
    style.textContent = `
      #touch-controls {
        position: fixed; inset: 0;
        z-index: 60; pointer-events: none;
        display: none;
      }
      #touch-controls.active { display: block; }

      /* ── Joystick ── */
      #joystick-zone {
        position: absolute; bottom: 20px; left: 20px;
        width: 150px; height: 150px;
        pointer-events: auto;
        touch-action: none;
      }
      #joystick-base {
        position: absolute; bottom: 0; left: 0;
        width: 130px; height: 130px;
        border-radius: 50%;
        background: rgba(255,255,255,0.08);
        border: 2px solid rgba(255,255,255,0.15);
        display: flex; align-items: center; justify-content: center;
      }
      #joystick-thumb {
        width: 50px; height: 50px;
        border-radius: 50%;
        background: radial-gradient(circle, rgba(255,255,255,0.4), rgba(255,255,255,0.15));
        border: 2px solid rgba(255,255,255,0.35);
        position: absolute;
        transition: none;
      }

      /* ── Buttons ── */
      #action-buttons {
        position: absolute; bottom: 20px; right: 16px;
        display: flex; flex-direction: column;
        align-items: flex-end; gap: 10px;
        pointer-events: auto;
        touch-action: none;
      }
      .weapon-buttons {
        display: flex; gap: 10px;
      }
      .action-btn {
        width: 62px; height: 62px;
        border-radius: 50%;
        border: 2px solid rgba(255,255,255,0.2);
        background: rgba(0,0,0,0.5);
        color: #fff; font-size: 26px;
        display: flex; align-items: center; justify-content: center;
        cursor: pointer;
        -webkit-tap-highlight-color: transparent;
        user-select: none;
        touch-action: manipulation;
        transition: background 0.15s, border-color 0.15s, transform 0.1s;
      }
      .action-btn:active, .action-btn.pressed {
        background: rgba(233,69,96,0.45);
        border-color: #e94560;
        transform: scale(0.92);
      }
      #btn-dash {
        width: 56px; height: 56px;
        font-size: 22px;
        border-color: rgba(100,181,246,0.3);
        background: rgba(33,150,243,0.2);
        align-self: flex-end;
        margin-bottom: 4px;
      }
      #btn-dash:active, #btn-dash.pressed {
        background: rgba(33,150,243,0.5);
        border-color: #64B5F6;
      }
      #btn-rock   { border-color: rgba(255,152,0,0.3); }
      #btn-paper  { border-color: rgba(76,175,80,0.3); }
      #btn-scissors { border-color: rgba(233,69,96,0.3); }
      #btn-rock:active, #btn-rock.pressed   { border-color: #FF9800; background: rgba(255,152,0,0.4); }
      #btn-paper:active, #btn-paper.pressed  { border-color: #4CAF50; background: rgba(76,175,80,0.4); }
      #btn-scissors:active, #btn-scissors.pressed { border-color: #e94560; background: rgba(233,69,96,0.4); }

      /* Smaller on very small screens */
      @media (max-width: 400px) {
        #joystick-base { width: 110px; height: 110px; }
        #joystick-thumb { width: 42px; height: 42px; }
        .action-btn { width: 54px; height: 54px; font-size: 22px; }
        #btn-dash { width: 48px; height: 48px; font-size: 18px; }
        #joystick-zone { width: 130px; height: 130px; }
      }
      @media (max-height: 500px) {
        #joystick-zone { bottom: 10px; left: 10px; }
        #action-buttons { bottom: 10px; right: 10px; }
        #joystick-base { width: 100px; height: 100px; }
        #joystick-thumb { width: 40px; height: 40px; }
        .action-btn { width: 50px; height: 50px; font-size: 20px; }
        #btn-dash { width: 44px; height: 44px; font-size: 16px; }
      }
    `;

    document.head.appendChild(style);
    document.body.appendChild(this.container);
  }

  _setupListeners() {
    const joyZone = document.getElementById('joystick-zone');
    const base = document.getElementById('joystick-base');
    const thumb = document.getElementById('joystick-thumb');
    const maxDist = 40; // max pixel displacement from center

    // ── Joystick touch ──
    joyZone.addEventListener('touchstart', (e) => {
      e.preventDefault();
      const t = e.changedTouches[0];
      this._joystickTouchId = t.identifier;
      const rect = base.getBoundingClientRect();
      this._joystickOrigin.x = rect.left + rect.width / 2;
      this._joystickOrigin.y = rect.top + rect.height / 2;
      this._joystickActive = true;
    }, { passive: false });

    joyZone.addEventListener('touchmove', (e) => {
      e.preventDefault();
      for (const t of e.changedTouches) {
        if (t.identifier !== this._joystickTouchId) continue;
        let dx = t.clientX - this._joystickOrigin.x;
        let dy = t.clientY - this._joystickOrigin.y;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist > maxDist) {
          dx = (dx / dist) * maxDist;
          dy = (dy / dist) * maxDist;
        }
        thumb.style.transform = `translate(${dx}px, ${dy}px)`;
        this.moveX = dx / maxDist;
        this.moveY = dy / maxDist;
      }
    }, { passive: false });

    const resetJoystick = (e) => {
      for (const t of e.changedTouches) {
        if (t.identifier !== this._joystickTouchId) continue;
        this._joystickTouchId = null;
        this._joystickActive = false;
        this.moveX = 0;
        this.moveY = 0;
        thumb.style.transform = 'translate(0, 0)';
      }
    };
    joyZone.addEventListener('touchend', resetJoystick, { passive: false });
    joyZone.addEventListener('touchcancel', resetJoystick, { passive: false });

    // ── Action buttons ──
    const setupBtn = (id, onDown, onUp) => {
      const btn = document.getElementById(id);
      btn.addEventListener('touchstart', (e) => {
        e.preventDefault();
        btn.classList.add('pressed');
        onDown();
      }, { passive: false });
      btn.addEventListener('touchend', (e) => {
        e.preventDefault();
        btn.classList.remove('pressed');
        if (onUp) onUp();
      }, { passive: false });
      btn.addEventListener('touchcancel', (e) => {
        btn.classList.remove('pressed');
        if (onUp) onUp();
      }, { passive: false });
    };

    setupBtn('btn-rock', () => {
      this._rockPressed = true;
      this._rockJust = true;
    });

    setupBtn('btn-paper',
      () => {
        this._paperHeld = true;
        this._paperJust = true;
      },
      () => { this._paperHeld = false; }
    );

    setupBtn('btn-scissors', () => {
      this._scissorsPressed = true;
      this._scissorsJust = true;
    });

    setupBtn('btn-dash', () => {
      this._dashPressed = true;
      this._dashJust = true;
    });
  }

  show() {
    this.container.classList.add('active');
  }

  hide() {
    this.container.classList.remove('active');
    this.moveX = 0;
    this.moveY = 0;
  }

  update() {
    // "justPressed" flags are consumed on each call
    // They accumulate between frames and get cleared after read
  }

  /**
   * Same signature as InputLocal.getActions(playerId).
   * Mobile only controls Player 0.
   */
  getActions(playerId) {
    if (playerId !== 0) {
      return { moveX: 0, moveY: 0, rock: false, paper: false, paperHold: false, scissors: false, dash: false };
    }

    const a = {
      moveX: Math.abs(this.moveX) > 0.15 ? this.moveX : 0,
      moveY: Math.abs(this.moveY) > 0.15 ? this.moveY : 0,
      rock: this._rockJust,
      paper: this._paperJust,
      paperHold: this._paperHeld,
      scissors: this._scissorsJust,
      dash: this._dashJust,
    };

    // Clear one-shot flags
    this._rockJust = false;
    this._paperJust = false;
    this._scissorsJust = false;
    this._dashJust = false;

    return a;
  }

  dispose() {
    if (this.container && this.container.parentNode) {
      this.container.parentNode.removeChild(this.container);
    }
  }
}
